import React, { useState, useEffect } from 'react';
import { Form, Row, Col, Input, InputNumber, DatePicker, Select, Divider, Alert, Card, Typography } from 'antd';
import { DollarOutlined, CheckCircleOutlined, DollarCircleOutlined, BankOutlined, CreditCardOutlined, FileTextOutlined, MoreOutlined, ClockCircleOutlined, CloseCircleOutlined } from '@ant-design/icons';
import moment from 'moment';
import '../../../../assets/scss/components/OfflineBooking/PaymentDetailsForm.scss';

const { Option } = Select;
const { TextArea } = Input;
const { Text, Title } = Typography;

/**
 * Enhanced Payment Details Form Component
 * 
 * Matches Backend Payment Model:
 * - bookingId: String (link to booking)
 * - amount: Double (payment amount)
 * - currency: String (e.g., LKR, USD)
 * - paymentMethod: String (CASH, BANK_TRANSFER, CREDIT_CARD, OTHER)
 * - paymentStatus: INITIATED | COMPLETED | CANCELLED
 * - paymentDescription: String (optional notes)
 * - paymentScheduleOn: Date (when payment is scheduled)
 * - paymentType: PaymentType enum
 */
const PaymentDetailsForm = ({ form, calculatedValues }) => {
  const [paymentAmount, setPaymentAmount] = useState(0);
  const [selectedPaymentStatus, setSelectedPaymentStatus] = useState('COMPLETED');

  // Auto-populate payment amount from calculated values
  useEffect(() => {
    if (calculatedValues && calculatedValues.finalBookingValue) {
      const amount = parseFloat(calculatedValues.finalBookingValue) || 0;
      setPaymentAmount(amount);
      form.setFieldsValue({
        paymentAmount: amount
      });
    }
  }, [calculatedValues, form]);

  return (
    <div className="payment-details-form">
      <Divider orientation="left">
        <DollarOutlined /> Payment Information
      </Divider>
      
      <Alert
        message="Record Payment Details"
        description="Capture payment information received from the client for this offline booking. You can update payment status later from the bookings list."
        type="info"
        showIcon
        style={{ marginBottom: 24, textAlign: 'left' }}
      />

      {/* Payment Amount Summary Card */}
      <Card 
        style={{ marginBottom: 24, backgroundColor: '#f0f5ff', borderColor: '#1890ff' }}
        size="small"
      >
        <Row gutter={16}>
          <Col span={12}>
            <Text strong>Booking Amount:</Text>
            <Title level={4} style={{ margin: '8px 0', color: '#1890ff' }}>
              {calculatedValues?.currency || 'LKR'} {calculatedValues?.finalBookingValue?.toLocaleString() || '0.00'}
            </Title>
          </Col>
          <Col span={12}>
            <Text strong>Payment Amount:</Text>
            <Title level={4} style={{ margin: '8px 0', color: '#52c41a' }}>
              {form.getFieldValue('currency') || 'LKR'} {paymentAmount?.toLocaleString() || '0.00'}
            </Title>
          </Col>
        </Row>
      </Card>

      <Row gutter={24}>
        <Col span={8}>
          <Form.Item
            name="paymentAmount"
            label="Payment Amount"
            rules={[
              { required: true, message: 'Please enter payment amount' },
              { type: 'number', min: 0, message: 'Amount must be positive' }
            ]}
            tooltip="Enter the actual payment amount received from client"
          >
            <InputNumber
              style={{ width: '100%' }}
              placeholder="Enter amount"
              precision={2}
              min={0}
              onChange={(value) => setPaymentAmount(value)}
              prefix={form.getFieldValue('currency') || 'LKR'}
            />
          </Form.Item>
        </Col>

        <Col span={8}>
          <Form.Item
            name="currency"
            label="Currency"
            rules={[{ required: true, message: 'Please select currency' }]}
          >
            <Select placeholder="Select currency" disabled>
              <Option value="LKR">LKR (Sri Lankan Rupee)</Option>
            </Select>
          </Form.Item>
        </Col>

        <Col span={8}>
          <Form.Item
            name="paymentMethod"
            label="Payment Method"
            initialValue="BANK_TRANSFER"
            rules={[{ required: true, message: 'Please select payment method' }]}
          >
            <Select placeholder="Select payment method" disabled>
              {/* <Option value="CASH"><DollarCircleOutlined /> Cash</Option> */}
              <Option value="BANK_TRANSFER"><BankOutlined /> Bank Transfer</Option>
              <Option value="CARD"><CreditCardOutlined /> Card</Option>
              {/* <Option value="CHEQUE"><FileTextOutlined /> Cheque</Option>
              <Option value="OTHER"><MoreOutlined /> Other</Option> */}
            </Select>
          </Form.Item>
        </Col>
      </Row>

      <Row gutter={24}>
        <Col span={12}>
          <Form.Item
            name="paymentScheduleOn"
            label="Payment Date"
            rules={[{ required: true, message: 'Please select payment date' }]}
            initialValue={moment()}
          >
            <DatePicker 
              style={{ width: '100%' }} 
              format="DD-MM-YYYY"
              placeholder="Select date"
              disabledDate={(current) => {
                if (!current) return false;
                const today = moment().endOf('day');
                const thirtyDaysAgo = moment().subtract(30, 'days').startOf('day');
                return current.isAfter(today) || current.isBefore(thirtyDaysAgo);
              }}
            />
          </Form.Item>
        </Col>

        <Col span={12}>
          <Form.Item
            name="transactionReference"
            label="Transaction Reference"
            tooltip="Optional reference number for tracking (e.g., bank transaction ID, receipt number)"
          >
            <Input 
              placeholder="e.g., TXN123456, BANK-REF-001"
              maxLength={100}
            />
          </Form.Item>
        </Col>
      </Row>

      <Row gutter={24}>
        <Col span={24}>
          <Form.Item
            name="paymentDescription"
            label="Payment Notes / Additional Details"
            rules={[
              { max: 500, message: 'Notes cannot exceed 500 characters' }
            ]}
          >
            <TextArea 
              rows={4} 
              placeholder="Add any notes about this payment (e.g., Transaction ID, bank details, receipt number, etc.)"
            />
          </Form.Item>
        </Col>
      </Row>

      <Divider orientation="left">
        <CheckCircleOutlined /> Payment Status
      </Divider>

      <Alert
        message="Payment Status Options"
        description={
          <ul style={{ marginBottom: 0, paddingLeft: 20 }}>
            <li><strong>INITIATED:</strong> Payment details recorded, awaiting confirmation or pending receipt</li>
            <li><strong>COMPLETED:</strong> Payment has been received and confirmed (booking will be locked after payment completion)</li>
          </ul>
        }
        type="warning"
        showIcon
        style={{ marginBottom: 24 ,textAlign: 'left' }}
      />

      <Row gutter={24}>
        <Col span={24}>
          <Form.Item
            name="paymentStatus"
            label="Payment Status"
            initialValue="COMPLETED"
            rules={[{ required: true, message: 'Please select payment status' }]}
            tooltip="Once payment is COMPLETED, booking cannot be edited"
          >
            <Select 
              placeholder="Select status"
              onChange={(value) => {
                setSelectedPaymentStatus(value);
                // Trigger validation on transaction reference when payment status changes
                form.validateFields(['transactionReference']);
              }}
            >
              <Option value="INITIATED">
                <ClockCircleOutlined style={{ color: '#faad14' }} /> INITIATED - Recorded, awaiting receipt
              </Option>
              <Option value="COMPLETED">
                <CheckCircleOutlined style={{ color: '#52c41a' }} /> COMPLETED - Payment confirmed (booking will be locked)
              </Option>
            </Select>
          </Form.Item>
        </Col>
      </Row>

      {/* <Alert
        message="Note"
        description="After creating the booking, you can update the payment status anytime from the Offline Booking List by clicking the 'Update Payment' button."
        type="info"
        style={{ marginTop: 16 }}
      /> */}
    </div>
  );
};

export default PaymentDetailsForm;
